"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { BiChevronLeft, BiChevronRight, BiRightArrowAlt } from "react-icons/bi";
import { FaWhatsapp } from "react-icons/fa";

export interface SlideData {
  id: number;
  image: string;
  title: string;
  highlightText?: string;
  description: string;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

interface SliderProps {
  slides: SlideData[];
  autoPlayDuration?: number;
}

export const Slider: React.FC<SliderProps> = ({
  slides,
  autoPlayDuration = 6000,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);

  const touchStartX = useRef<number>(0);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
    setProgress(0);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slides.length) % slides.length);
    setProgress(0);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setProgress(0);
  };

  // Main Timer
  useEffect(() => {
    if (!slides.length) return;

    const slideTimer = setInterval(() => {
      nextSlide();
    }, autoPlayDuration);

    return () => clearInterval(slideTimer);
  }, [autoPlayDuration, nextSlide, slides.length]);

  // Smooth Progress Bar
  useEffect(() => {
    setProgress(0);
    const updateInterval = 50;
    const step = (updateInterval / autoPlayDuration) * 100;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + step;
      });
    }, updateInterval);

    return () => clearInterval(progressTimer);
  }, [currentIndex, autoPlayDuration]);

  // Touch Support
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (diff > 50) nextSlide();
    if (diff < -50) prevSlide();
  };

  const formattedCurrentIndex = String(currentIndex + 1).padStart(2, "0");
  const formattedTotalSlides = String(slides.length).padStart(2, "0");

  return (
    <div
      className="relative w-full h-[450px] sm:h-[550px] lg:h-[calc(120vh-135px)] min-h-[400px] lg:min-h-[550px] lg:max-h-[850px] overflow-hidden select-none bg-black text-white font-sans"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top Progress Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/20 z-40">
        <div
          className="h-full bg-[#ff9800] transition-all duration-75 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Slide Container (Flex Track) */}
      <div
        className="flex w-full h-full transition-transform duration-700 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => {
          const isActive = index === currentIndex;

          return (
            <div
              key={slide.id}
              className="relative w-full min-w-full flex-shrink-0 flex-grow-0 basis-full h-full overflow-hidden"
            >
              {/* Background Image */}
              <div
                className={`absolute inset-0 bg-cover bg-center transition-transform duration-[7000ms] ease-out ${
                  isActive ? "scale-105" : "scale-100"
                }`}
                style={{ backgroundImage: `url(${slide.image})` }}
              />

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60" />

              {/* Content Container */}
              <div className="relative z-20 h-full max-w-7xl mx-auto px-5 sm:px-10 md:px-12 flex flex-col justify-center pt-8 pb-16 lg:py-0">
                <div className="max-w-3xl">
                  {/* Heading */}
                  <h1
                    className={`text-xl sm:text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-snug transition-all duration-700 ease-out ${
                      isActive
                        ? "translate-x-0 opacity-100 delay-200"
                        : "-translate-x-12 opacity-0"
                    }`}
                  >
                    <div>{slide.title}</div>
                    {slide.highlightText && (
                      <div className="text-[#ff9800] mt-0.5">{slide.highlightText}</div>
                    )}
                  </h1>

                  {/* Description */}
                  <p
                    className={`mt-2 sm:mt-4 text-xs sm:text-sm md:text-base lg:text-lg text-neutral-200 leading-relaxed font-normal transition-all duration-700 ease-out max-w-2xl line-clamp-2 lg:line-clamp-none ${
                      isActive
                        ? "translate-x-0 opacity-100 delay-400"
                        : "-translate-x-12 opacity-0"
                    }`}
                  >
                    {slide.description}
                  </p>

                  {/* Buttons */}
                  <div
                    className={`mt-4 sm:mt-6 lg:mt-8 flex flex-wrap items-center gap-2.5 sm:gap-4 transition-all duration-700 ease-out ${
                      isActive
                        ? "translate-x-0 opacity-100 delay-600"
                        : "-translate-x-12 opacity-0"
                    }`}
                  >
                    {slide.primaryButtonText && (
                      <a
                        href={slide.primaryButtonLink || "#"}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 sm:px-6 sm:py-3 bg-[#ff9800] hover:bg-[#e68a00] active:scale-95 text-white font-extrabold text-[11px] sm:text-xs lg:text-sm tracking-wider uppercase transition-all shadow-md rounded-none"
                      >
                        <BiRightArrowAlt className="w-4 h-4 sm:w-5 sm:h-5" />
                        {slide.primaryButtonText}
                      </a>
                    )}

                    {slide.secondaryButtonText && (
                      <a
                        href={slide.secondaryButtonLink || "#"}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 sm:px-6 sm:py-3 border border-white hover:border-[#ff9800] hover:bg-[#ff9800] hover:text-white bg-black/40 active:scale-95 text-white font-extrabold text-[11px] sm:text-xs lg:text-sm tracking-wider uppercase transition-all shadow-md rounded-none"
                      >
                        {slide.secondaryButtonLink?.toLowerCase().includes("whatsapp") ||
                        slide.secondaryButtonText.toLowerCase().includes("contact") ? (
                          <FaWhatsapp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        ) : null}
                        {slide.secondaryButtonText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Counter Number */}
      <div className="absolute bottom-3 left-4 sm:bottom-6 sm:left-10 lg:bottom-8 lg:left-12 z-30 font-extrabold text-xs sm:text-xl lg:text-3xl tracking-widest text-white/90">
        {formattedCurrentIndex}
        <span className="text-white/40 text-[10px] sm:text-base lg:text-2xl font-normal">
          /{formattedTotalSlides}
        </span>
      </div>

      {/* Navigation Arrow Buttons */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-30 flex items-center justify-center">
        <div className="relative bg-[#ff9800] px-3.5 py-1.5 sm:px-6 sm:py-2.5 lg:px-8 lg:py-3 flex items-center gap-3 sm:gap-6 [clip-path:polygon(15%_0%,85%_0%,100%_100%,0%_100%)] min-w-[100px] sm:min-w-[140px] lg:min-w-[160px] justify-center">
          <button
            onClick={prevSlide}
            aria-label="Previous slide"
            className="text-white hover:scale-125 active:scale-90 transition-transform"
          >
            <BiChevronLeft className="w-4 h-4 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
          </button>
          <div className="w-[1px] h-3 sm:h-4 lg:h-5 bg-white/40" />
          <button
            onClick={nextSlide}
            aria-label="Next slide"
            className="text-white hover:scale-125 active:scale-90 transition-transform"
          >
            <BiChevronRight className="w-4 h-4 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
          </button>
        </div>
      </div>

      {/* Side Dots (Desktop only) */}
      <div className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-30 hidden lg:flex flex-col gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? "w-3 h-3 bg-white ring-4 ring-white/30"
                : "w-3 h-3 bg-white/40 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
};